"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AttributedLink } from "@/components/marketing/attributed-link";

export interface StickyMobileCtaTarget {
  href: string;
  label: string;
}

/**
 * Keep the global mobile CTA aligned with the commercial job of the page.
 * Premium visitors should never be pulled out of a £199 checkout or a
 * sales-led Site Control journey and dropped into the free-project funnel.
 */
export function stickyMobileCtaTarget(pathname: string): StickyMobileCtaTarget {
  if (pathname === "/business" || pathname.startsWith("/business/")) {
    return {
      href: "#business-checkout",
      label: "Start Business for £199/month",
    };
  }

  if (pathname === "/site-control" || pathname.startsWith("/site-control/")) {
    return { href: "#book", label: "Book a Site Control walkthrough" };
  }

  if (
    pathname === "/principal-contractors" ||
    pathname.startsWith("/principal-contractors/")
  ) {
    return {
      href: "/site-control#book",
      label: "Book a Site Control walkthrough",
    };
  }

  // Feature pages route into the /start-project funnel motion (Stage 3): the
  // in-page acquisition CTAs do, and the sticky bar must not split the same
  // visitor's mobile journey back to the bare signup form. Every other
  // ordinary marketing route keeps the plain signup default.
  if (pathname === "/features" || pathname.startsWith("/features/")) {
    return { href: "/start-project", label: "Start your first project" };
  }

  return { href: "/signup", label: "Start your first project" };
}

/**
 * Sticky bottom CTA bar — mobile only (hidden on md+).
 * Appears when the user scrolls past the hero sentinel (≈ top of page content).
 * z-40 so it sits above cookie banners but below modals (z-50+).
 */
export function StickyMobileCta() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const target = stickyMobileCtaTarget(pathname);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Show CTA once the sentinel (hero-height placeholder) leaves the viewport
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/*
       * Sentinel: a zero-width, hero-height div at the top of the page.
       * When this scrolls out of view the CTA slides in.
       * pointer-events-none so it never interferes with layout.
       */}
      <div
        ref={sentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 h-[500px] w-0"
      />

      {/* Sticky mobile bar */}
      <div
        className={[
          "fixed bottom-0 left-0 right-0 z-40",
          "border-t border-border bg-surface px-4 pb-4 pt-3 shadow-lg",
          "transition-transform duration-300 ease-in-out",
          "md:hidden", // desktop: never shown
          visible ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
        // inert (not just aria-hidden) so the off-screen signup link is also
        // removed from the tab order — an aria-hidden element must not
        // contain focusable children
        inert={!visible}
      >
        <AttributedLink
          href={target.href}
          className="flex min-h-[44px] w-full items-center justify-center rounded-lg bg-accent px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          {target.label} &rarr;
        </AttributedLink>
      </div>
    </>
  );
}
